import {Text, Tile, Toast} from "@wakelet/ui-kit";
import {FC, ReactNode, useEffect, useState} from "react";
import {useAuth} from "../auth/AuthContext";
import {ChangeEmail} from "./ChangeEmail";
import {ChangePassword} from "./ChangePassword";
import {UserAvatar} from "../user/UserAvatar";
import {toProviderIcon} from "../sso/icons";
import {AuthProviderType, toAuthProviderName} from "../../domain/providers";
import {FullPageSpinner} from "../loading/Spinner";
import {DeleteAccountTile} from "../delete-account/DeleteAccountTile";
import {AccountsAPI} from "../../accounts-api/accounts-api";
import {useNavigate} from "react-router";
import {Preferences} from "./Preferences";
import {ExportContent} from "./ExportContent";
import {getAccountApiUrl} from "../../env/env";
import {translateKey} from "../language/translate";
import {useAccountPermissions} from "./useAccountPermissions";

const settingsUrl = getAccountApiUrl("/user-preferences");

export const UserSettings = () => {
  const {user} = useAuth();
  const profile = useAccountPermissions(user);
  const isEmailPasswordUser = user?.provider === AuthProviderType.Password;
  useEffect(() => {
    if (!user) {
      return
    }
    const handleLangChanged = (async (e: CustomEvent) => {
      const idToken = await user.getIdToken();
      await fetch(settingsUrl, {
        method: "PUT",
        body: JSON.stringify({language: e.detail}),
        headers: {
          Authorization: `Bearer ${idToken}`
        }
      })

    }) as unknown as EventListener;
    window.addEventListener("language-changed", handleLangChanged)
    return () => {
      window.removeEventListener("language-changed", handleLangChanged)
    }
  }, [user])
  if (!user) {
    return <FullPageSpinner/>
  }
  return (
    <div className="flex flex-col items-center w-full" data-testid={"user-settings"}>
      <div className="m-auto w-20">
        <UserAvatar
          displayName={user?.displayName}
          email={user?.email}
          photoURL={user?.photoUrl}
        />
      </div>
      <div className="mt-5 text-center">
        <Text.Heading
          level={3}>{translateKey("account:settings.heading", {name: user?.displayName || ""})}</Text.Heading>
        <Text.Caption>{translateKey("account:settings.caption")}</Text.Caption>
      </div>

      <div className="mt-5 w-full">
        <UserBanner
          email={user.email || user.displayName || ""}
          icon={toProviderIcon(user.provider)}
          provider={toAuthProviderName(user.provider)}/>
      </div>
      {isEmailPasswordUser && <div className="mt-5 w-full"><ChangeEmail/></div>}
      {isEmailPasswordUser && <div className="mt-5 w-full"><ChangePassword/></div>}
      {profile?.canSwitchToEdu && <div className="mt-5 w-full"><Preferences/></div>}
      <div className="mt-5 w-full"><ExportContent/></div>
      {profile?.canDelete && <div className="mt-5 w-full"><DeleteAccount/></div>}

    </div>
  );
};

type UserBannerProps = {
  email: string
  provider: string
  icon: ReactNode
}

export const UserBanner: FC<UserBannerProps> = ({email, provider, icon}) => {
  return <Tile>
    <div className="flex flex-wrap justify-between content-center gap-2">
      <Text
        className="flex items-center gap-2">{icon} {translateKey("account:settings.signedInWithProvider", {provider})}</Text>
      <Text>{email}</Text>
    </div>
  </Tile>;
};

const DeleteAccount = () => {
  const [open, setOpen] = useState(false)
  const navigateTo = useNavigate()
  const {user} = useAuth();

  async function onDelete() {
    const idToken = await user?.getIdToken(true)
    if (!idToken) {
      return
    }
    const {deleted} = await AccountsAPI.deleteAccount(idToken)
    if (deleted) {
      navigateTo("/")
    } else {
      setOpen(true)
    }
  }

  return (
    <>
      <DeleteAccountTile onDelete={onDelete}/>
      <Toast type={"error"}
             description={"There was an error while deleting your account"}
             open={open}
      />
    </>
  )
}
